/**
 * QueueView - per-position CAP list. Mounted at
 * /framework/queue/:position. The PositionQueueWidget pills (P1.2.e)
 * link here, and the CAPDetailView's back-link returns here.
 *
 * Renders a table of CAPSummary rows (no per-CAP fetch - the list
 * endpoint already carries everything the row needs). Each row is
 * a router Link to the CAP detail.
 *
 * Position gate: the route param IS the position; we don't filter
 * on hasPosition here because reviewers + approvers legitimately
 * need to peek at the preparer queue (read-only) when reviewing a CAP's
 * upstream history. The position-aware action buttons live in
 * CAPDetailView, not on the row.
 *
 * Empty state: a friendly "queue is empty" message so the operator
 * knows the API call succeeded with zero rows (vs the loading
 * placeholder).
 */
import { RegulatorTag } from "shared/components/RegulatorTag";
import { formatDateTime } from "shared/time/formatDate";
import { Figure } from "shared/components/Figure";
import { i18n } from "shared/i18n";
import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { roleLabel } from "shared/vocab/workflowState";

import { useCaps } from "framework/api/hooks";
import { useAssessmentQueue } from "framework/api/assessment_hooks";
import { frameworkLabel, stateLabel } from "shared/vocab/workflowState";
import { PositionPill, StatePill } from "framework/components/StatePill";
import { useSetLastRole, useUserPreferences } from "shared/api/userPreferences";
import { ALL_POSITIONS, type Position, type CAPState } from "shared/api/types";

function isPosition(value: string | undefined): value is Position {
  return value !== undefined && (ALL_POSITIONS as readonly string[]).includes(value);
}

export function QueueView() {
  const { t } = useTranslation();
  const { position: raw } = useParams<{ position: string }>();
  // useCaps must be called unconditionally (Rules of Hooks). Pass
  // undefined for unknown positions; the hook still runs but we
  // render the error branch below instead of its data.
  const position: Position | undefined = isPosition(raw) ? raw : undefined;
  const caps = useCaps(position);
  // B2 -- the queue listed CAPs and nothing else, so a preparer who
  // submitted an assessment could not follow it anywhere: her first
  // hour ended without her knowing where it went. Carried from rounds
  // 1 and 2.
  const assessments = useAssessmentQueue(position);

  // P115.5: record the position the user is working in as the
  // default-landing hint (only when it changed, to avoid churn).
  const prefs = useUserPreferences();
  const setRole = useSetLastRole();
  const setRoleMutate = setRole.mutate;
  const storedRole = prefs.data?.last_role_used;
  useEffect(() => {
    if (position && prefs.data && storedRole !== position) {
      setRoleMutate(position);
    }
  }, [position, storedRole, prefs.data, setRoleMutate]);

  if (!position) {
    return (
      <article className="prose max-w-3xl">
        <p className="text-red-700">
          {t("screens.queueView.unknownQueuePosition")} {JSON.stringify(raw)}
          {t("screens.queueView.expectedOneOfPreparerReviewerApprover")}
        </p>
      </article>
    );
  }

  if (caps.isLoading) {
    return (
      <div className="text-sm text-slate-500">
        {t("screens.queueView.loadingQueue")}
      </div>
    );
  }
  if (caps.isError) {
    return (
      <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
        {caps.error.detail}
      </p>
    );
  }

  const rows = caps.data ?? [];
  const assessmentRows = assessments.data ?? [];

  return (
    <article className="max-w-5xl">
      <header className="flex items-center gap-3 mb-5">
        {/* Round 10 E1, fourth round on this heading. Two faults in
            one line: `capitalize` is a Latin instruction that does
            nothing for Arabic and mis-cases a translated word, and the
            POSITION was interpolated as a raw enum, so an Arabic
            reader read "⁨preparer⁩" inside an Arabic sentence. The
            catalogue has had the seat's name since round 9. */}
        <h1 className="text-2xl font-semibold">
          {t("caps.queue.heading", { position: roleLabel(position) })}
        </h1>
        <PositionPill position={position} label={t(`queue.${position}`)} />
      </header>
      <p
        data-testid="queue-standfirst"
        className="-mt-3 mb-5 max-w-3xl text-sm text-slate-600"
      >
        {t(`caps.queue.standfirst.${position}`)}
      </p>

      {rows.length === 0 && assessmentRows.length === 0 ? (
        <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded p-4">
          {t("caps.queue.empty")}
        </p>
      ) : rows.length === 0 ? null : (
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-start text-slate-600 border-b border-slate-200">
              <th className="py-2 pe-3">{t("caps.queue.column.title")}</th>
              <th className="py-2 pe-3">{t("caps.queue.column.letter")}</th>
              <th className="py-2 pe-3">{t("caps.queue.column.state")}</th>
              <th className="py-2 pe-3 text-right">{t("caps.queue.column.entries")}</th>
              <th className="py-2 pe-3">{t("caps.queue.column.updated")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((cap) => (
              <tr key={cap.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-2 pe-3">
                  <Link
                    to={`/framework/caps/${cap.id}`}
                    className="text-brand-brassDark hover:underline font-medium"
                  >
                    {cap.title}
                  </Link>
                </td>
                <td className="py-2 pe-3 text-slate-700">
                  <RegulatorTag code={cap.letter_regulator_code} />
                  {cap.letter_title}
                </td>
                <td className="py-2 pe-3">
                  <StatePill
                    state={cap.state as CAPState}
                    label={t(`caps.detail.state_labels.${cap.state}`)}
                  />
                </td>
                <td className="py-2 pe-3 text-right tabular-nums">{cap.entry_count}</td>
                <td className="py-2 pe-3 text-slate-600 tabular-nums">
                  {formatDateTime(cap.updated_at, i18n.language)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {assessmentRows.length > 0 && (
        <section data-testid="queue-assessments" className="mt-6">
          <h2 className="mb-2 text-sm font-semibold text-slate-800">
            {t("caps.queue.assessmentsHeading", { count: assessmentRows.length })}
          </h2>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-start text-slate-600 border-b border-slate-200">
                <th className="py-2 pe-3">{t("caps.queue.column.control")}</th>
                <th className="py-2 pe-3">{t("caps.queue.column.cycle")}</th>
                <th className="py-2 pe-3">{t("caps.queue.column.state")}</th>
                <th className="py-2 pe-3">{t("caps.queue.column.updated")}</th>
              </tr>
            </thead>
            <tbody>
              {assessmentRows.map((a) => (
                <tr
                  key={a.id}
                  data-testid="queue-assessment-row"
                  className="border-b border-slate-100 hover:bg-slate-50"
                >
                  <td className="py-2 pe-3">
                    <Link
                      // Round 17 G20 -- the control the reviewer is asked
                      // about, opened, not the cycle's front page.
                      to={`/framework/assessments/${a.framework_code}/cycles/${a.cycle_id}?control=${encodeURIComponent(a.framework_control_id)}`}
                      className="font-medium text-brand-brassDark hover:underline"
                    >
                      {/* D2 -- a control code is a figure, not prose.
                          Beside an English control text inside a
                          right-to-left row it rendered "CR7-3.1.1" and,
                          in the reviewer's queue, tore in half: "-3.1.1"
                          at one edge and "CR8" glued to the sentence at
                          the other. */}
                      <Figure>{a.control_natural_id}</Figure>
                    </Link>
                    {" · "}
                    <span className="ms-2 text-xs text-slate-500" dir="auto">
                      {a.control_text}
                    </span>
                  </td>
                  <td className="py-2 pe-3 text-slate-700">
                    <span className="me-1 text-xs text-slate-500">
                      [{frameworkLabel(a.framework_code)}]
                    </span>{" "}
                    {a.cycle_name}
                  </td>
                  <td className="py-2 pe-3 text-slate-700">
                    {stateLabel(a.workflow_state)}
                  </td>
                  <td className="py-2 pe-3 tabular-nums text-slate-600">
                    {formatDateTime(a.updated_at, i18n.language)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </article>
  );
}
